/**
 * Toast capture script injected into pages.
 *
 * @module playwright-client/toast-capture-script
 * @see docs/api/services/browser-session.md
 */

/**
 * MutationObserver script injected into every page to capture ephemeral
 * toast notifications and ARIA live region announcements.
 */
export const TOAST_CAPTURE_SCRIPT = `
(() => {
  if (window.__toastObserverInstalled) return;
  window.__toastObserverInstalled = true;
  window.__capturedToasts = [];

  // Selectors that directly identify a toast/notification element.
  // Covers ARIA standards + the most popular React/Vue/Angular toast libraries.
  // When adding new selectors, verify they don't match persistent UI (navbars, footers).
  //
  // TODO: Consider a position-based heuristic as a catch-all for unknown libraries.
  // Most toasts share a visual pattern: a fixed/absolute-positioned element appearing
  // temporarily near a screen edge. Detecting elements added to body/portals with
  // position:fixed and bounded dimensions could catch toasts regardless of class names,
  // but risks false positives from dropdowns, modals, and tooltips. Would need filtering
  // by size, lifetime, and z-index to be practical.
  const TOAST_SELECTORS = [
    // ARIA standards
    '[role="alert"]',
    '[role="status"]',
    '[aria-live="polite"]',
    '[aria-live="assertive"]',
    // Generic class names
    '.toast',
    '.notification',
    '[data-toast]',
    // react-toastify
    '.Toastify__toast',
    // Chakra UI
    '.chakra-toast',
    '.chakra-alert',
    // Ant Design
    '.ant-message-notice',
    '.ant-notification-notice',
    // Material UI
    '.MuiSnackbar-root',
    '.MuiAlert-root',
    // notistack (MUI companion)
    '.notistack-SnackbarContainer',
    '.notistack-MuiContent',
    // Sonner
    '[data-sonner-toast]',
    '[data-sonner-toaster]',
    // Radix UI
    '[data-radix-toast-viewport]',
    // React Hot Toast
    '[data-hot-toast]',
    // Mantine
    '.mantine-Notification-root',
    // PrimeReact
    '.p-toast-message',
    // Blueprint.js
    '.bp4-toast',
    '.bp5-toast',
    // Headless UI (Tailwind)
    '[data-headlessui-state] [role="alert"]',
    // Vue - Element Plus
    '.el-notification',
    '.el-message',
    // Vue - Vuetify
    '.v-snackbar__content',
    // Angular Material
    '.mat-mdc-snack-bar-container',
    'mat-snack-bar-container',
    // ngx-toastr (Angular)
    '.toast-container .ngx-toastr',
    // AngularJS custom alert directives
    '[alert-instance]',
    '.alert-wrapper',
    // Generic alert containers (transient, not Bootstrap static .alert)
    '[class*="alerts-container"]',
    '[class*="alert-container"]',
    // jQuery Toast Plugin
    '.jq-toast-wrap',
    '.jq-toast-single',
    // Rails / server-rendered flash messages
    '.flash-message',
    '.flash-notice',
    '.flash-alert',
  ].join(',');

  // Selectors for persistent live-region containers. Toasts are often added
  // as children of a container with aria-live that is mounted once at startup.
  // We need to detect new children added inside these containers.
  const LIVE_REGION_CONTAINER = '[aria-live="polite"], [aria-live="assertive"], [class*="alerts-container"], [class*="alert-container"], .jq-toast-wrap';

  const MAX_TOASTS = 20;

  // Extract meaningful text from an element, excluding dismiss buttons and icons
  function extractToastText(el) {
    // Try to find the main message element first (common patterns)
    const messageEl = el.querySelector('p, [class*="message"], [class*="content"], [class*="body"]');
    if (messageEl) return (messageEl.textContent || '').trim();
    // Clone and remove buttons/controls to avoid "X" dismiss button text leaking in
    const clone = el.cloneNode(true);
    const controls = clone.querySelectorAll('button, [role="button"], svg, [aria-hidden="true"]');
    for (const ctrl of controls) ctrl.remove();
    return (clone.textContent || '').trim();
  }

  function pushToast(text, role, el) {
    if (!text || text.length === 0 || text.length >= 1000) return;
    // Deduplicate: skip if we already have the same text from the last 2 seconds
    const now = Date.now();
    const isDupe = window.__capturedToasts.some(
      (t) => t.text === text && now - t.time < 2000
    );
    if (isDupe) return;

    let placement = null;
    if (el && el.getBoundingClientRect) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        placement = {
          x: Math.round(rect.x),
          y: Math.round(rect.y),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        };
      }
    }

    window.__capturedToasts.push({ text: text, role: role, time: now, placement: placement });
    if (window.__capturedToasts.length > MAX_TOASTS) {
      window.__capturedToasts.shift();
    }
  }

  // --- Secondary observer for CSS-toggled toasts ---

  function isElementVisible(el) {
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function captureVisibleToast(el) {
    if (!el || el.nodeType !== 1) return;
    // If the element itself matches a toast selector and is visible, capture it
    if (el.matches && el.matches(TOAST_SELECTORS) && isElementVisible(el)) {
      const text = extractToastText(el);
      if (text) {
        pushToast(text, el.getAttribute('role') || el.getAttribute('aria-live') || 'toast', el);
        return;
      }
    }
    // Check descendants
    if (el.querySelectorAll) {
      const descendants = el.querySelectorAll(TOAST_SELECTORS);
      for (const desc of descendants) {
        if (isElementVisible(desc)) {
          const text = extractToastText(desc);
          if (text) {
            pushToast(text, desc.getAttribute('role') || desc.getAttribute('aria-live') || 'toast', desc);
          }
        }
      }
    }
  }

  const observedContainers = new WeakSet();

  function installContainerObserver(container) {
    if (!container || observedContainers.has(container)) return;
    observedContainers.add(container);

    const containerObs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList') {
          for (const node of m.addedNodes) {
            captureNode(node);
          }
        } else if (m.type === 'attributes') {
          captureVisibleToast(m.target);
        } else if (m.type === 'characterData') {
          // Text content changed (e.g., jq-toast reusing same element with new message)
          const parent = m.target.parentElement;
          if (parent) captureVisibleToast(parent);
        }
      }
    });

    containerObs.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
      characterData: true,
    });
  }

  function captureNode(node) {
    if (node.nodeType !== 1) return;
    const el = node;
    if (!el.matches) return;

    // Case 1: The added node itself is a toast element
    if (el.matches(TOAST_SELECTORS)) {
      pushToast(
        extractToastText(el),
        el.getAttribute('role') || el.getAttribute('aria-live') || 'toast',
        el
      );
      // Install secondary observer if this is also a live-region container
      if (el.matches(LIVE_REGION_CONTAINER)) {
        installContainerObserver(el);
      }
      return; // Don't double-capture children
    }

    // Case 2: The added node contains toast elements as descendants
    if (el.querySelectorAll) {
      const children = el.querySelectorAll(TOAST_SELECTORS);
      for (const child of children) {
        pushToast(
          extractToastText(child),
          child.getAttribute('role') || child.getAttribute('aria-live') || 'toast',
          child
        );
      }
      if (children.length > 0) return;
    }

    // Case 3: The added node is inside a live-region container (e.g. a toast
    // rendered as a child of a persistent aria-live div). The node itself won't
    // have ARIA attrs — it's just a <div> with text content.
    if (el.closest && el.closest(LIVE_REGION_CONTAINER)) {
      const container = el.closest(LIVE_REGION_CONTAINER);
      const text = extractToastText(el);
      if (text) {
        const role = container
          ? container.getAttribute('role') || container.getAttribute('aria-live') || 'live-region'
          : 'live-region';
        pushToast(text, role, el);
      }
      // Install secondary observer on the container for future CSS toggles
      installContainerObserver(container);
    }
  }

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        captureNode(node);
      }
    }
  });

  function scanPreExistingContainers() {
    const containers = document.querySelectorAll(LIVE_REGION_CONTAINER);
    for (const container of containers) {
      installContainerObserver(container);
      // Capture any currently-visible toasts inside pre-existing containers
      captureVisibleToast(container);
    }
  }

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
    scanPreExistingContainers();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
      scanPreExistingContainers();
    });
  }
})();
`;
